export const PAGE_ROUTES = {
  DASHBOARD: "view_dashboard",
  /** Dashboard talk tiles: show group name chips when multiple groups are on the page. */
  VIEW_DASHBOARD_TILE_GROUPS: "view_dashboard_tile_groups",
  /** Dashboard talk tiles: show client name chips when multiple clients are on the page. */
  VIEW_DASHBOARD_TILE_CLIENTS: "view_dashboard_tile_clients",
  /** Dashboard progress widget: completed talks, hours of training, conversations, last practice. */
  VIEW_DASHBOARD_PROGRESS: "view_dashboard_progress",
  /** Dashboard: show the active/inactive scenario filter (otherwise active-only). */
  VIEW_INACTIVE_SCENARIOS: "view_inactive_scenarios",
  /** Talks library tiles: access inactive TALKS — gates the active/inactive actor filter. */
  START_INACTIVE_ACTOR: "start_inactive_actors",
  TALKS_LIBRARY_TILES: "view_talks_library_tiles",
  TRAINING: "view_training",
  ACCOUNT_SETTINGS: "view_account_setting",
  PRIVACY_SETTINGS: "view_privacy_settings",
  PREFERENCES_SETTINGS: "view_preferences_settings",
  USER: "view_user",
  SIDEBAR: "view_sidebar",
  STATISTICS: "view_statistics",
  ROLES: "view_role",
  GROUPS: "view_groups",
  CLIENT_MANAGEMENT: "view_client_management",
  LTI_MANAGEMENT: "view_lti_management",
  PROGRESS_USER: "view_progress_user",
  EVENT_PROGRESS: "view_event_progress",
  CONVERSATION: "view_conversation",
  TALKS_FORMS: "view_talks_forms",
  TALKS_WIZARD: "view_talks_wizard",
  TALKS_EDITOR: "view_talks_editor",
  /**
   * Coach assistant. The permission alone is not enough: the coach is a per-client
   * module, so the UI additionally requires customizations.coach.enabled on one of
   * the user's clients/groups (see useCoachEnabled) — same rule the backend applies.
   */
  COACH: "coach_access",
  /** Maintenance: coaching instructions, model, report definitions, skill background. */
  MANAGE_COACH_SETTINGS: "manage_coach_settings",
  /** Coach background, tiles and instructions — the umbrella grants client AND group level. */
  MANAGE_COACH_KNOWLEDGE: "manage_coach_knowledge",
  /** The same, for a client's (company's) level only. */
  MANAGE_COACH_CLIENT_KNOWLEDGE: "manage_coach_client_knowledge",
  /** The same, for a group's (course's) level only. */
  MANAGE_COACH_GROUP_KNOWLEDGE: "manage_coach_group_knowledge",
  /**
   * Maintain your OWN Personalisation (user level). Not enough on its own: the
   * client and the event must both have personalisationEnabled — unlike every
   * other coach flag that rule is conjunctive, so a client switching it off
   * vetoes every event beneath it (see coachPersonalisationService).
   */
  COACH_PERSONALISATION: "coach_personalisation",
  /** Connection check (browser, microphone, network, test conversation) — granted per role. */
  READINESS_CHECK: "run_readiness_check",
  /** Stored connection checks, for support (scoped to the clients one may see). */
  VIEW_READINESS_CHECKS: "view_readiness_checks",
  /** TALKS tools that can be handed out without super-admin rights (scoped to the clients one may see). */
  TALK_TOOLS: {
    COPY_CLIENT: "copy_client",
    VIEW_SETTINGS: "view_settings_overview",
    EDIT_SETTINGS: "edit_settings_overview",
  },
  SIDEBAR_PERMISSIONS: {
    COACH: "sidebar_coach",
    MAINTENANCE_COACH: "manage_coach_settings",
    DASHBOARD: "sidebar_dashboard",
    MANAGEMENT: "sidebar_management",
    CONVERSATION: "sidebar_conversation",
    PROGRESS_USER: "sidebar_progress_user",
    EVENT_PROGRESS: "sidebar_event_progress",
    PROGRESS_GROUPS: "sidebar_progress_groups",
    MANAGEMENT_USER: "sidebar_management_user",
    MANAGEMENT_ROLE: "sidebar_management_role",
    MANAGEMENT_GROUPS: "sidebar_management_groups",
    MANAGEMENT_CLIENTS: "sidebar_management_clients",
    MANAGEMENT_LTI: "sidebar_management_lti",
    MANAGEMENT_NEWS: "news_manage",
    ACCOUNT_SETTINGS: "sidebar_account_settings",
    STATISTICS: "sidebar_statistics",
    PREFERENCES_SETTINGS: "sidebar_preferences_settings",
    PRIVACY_SETTINGS: "sidebar_privacy_settings",
    CONTENT: "sidebar_content",
    TALKS_LIBRARY: "sidebar_talks_library",
    TALKS_LIBRARY_TILES: "sidebar_talks_library_tiles",
    SETTINGS: "sidebar_settings",
    MAINTENANCE: "view_audit_logs",
    MAINTENANCE_AUDIT_TRAIL: "sidebar_management_audit_logs",
    MAINTENANCE_AI_INCIDENTS: "sidebar_maintenance_ai_incidents",
    MAINTENANCE_AI_USAGE: "sidebar_management_ai_usage",
    MAINTENANCE_CRON: "sidebar_maintenance_cron",
    READINESS_CHECK: "sidebar_readiness_check",
    MAINTENANCE_READINESS_CHECKS: "view_readiness_checks",
  },
  STATISTICS_SECTIONS: {
    OVERVIEW_CLIENT_LEVEL: "statistics_overview_client_level",
    OVERVIEW_USER_LEVEL: "statistics_overview_user_level",
    OVERVIEW_GROUP_LEVEL: "statistics_overview_group_level",
    SCENARIO_CONVERSATION_COUNTS_CLIENT_LEVEL:
      "statistics_scenario_conversation_counts_client_level",
    SCENARIO_CONVERSATION_COUNTS_USER_LEVEL:
      "statistics_scenario_conversation_counts_user_level",
    SCENARIO_CONVERSATION_COUNTS_GROUP_LEVEL:
      "statistics_scenario_conversation_counts_group_level",
    SCENARIO_REQUEST_COUNTS_CLIENT_LEVEL:
      "statistics_scenario_request_counts_client_level",
    SCENARIO_REQUEST_COUNTS_USER_LEVEL:
      "statistics_scenario_request_counts_user_level",
    SCENARIO_REQUEST_COUNTS_GROUP_LEVEL:
      "statistics_scenario_request_counts_group_level",
  },
  GROUP_PERMISSIONS: {
    MANAGE_CONVERSATION_APPS: "group_manage_conversation_apps",
    /**
     * An event's TALKS call settings. Separate from edit_group because it is handed out per
     * client, and separate from MANAGE_CONVERSATION_APPS because that one means "which TALKS app
     * runs this event", not "how the call looks". The backend enforces it in
     * patchGroupCustomizations for any patch touching conv_app_settings.
     */
    MANAGE_CALL_SETTINGS: "manage_group_call_settings",
    ASSIGN_ANONYMOUS_INTERVAL: "assign_anonymous_interval_to_group",
    ASSIGN_GROUP_TO_USER: "assign_group_to_user",
    VIEW_GROUP_COLUMN_IN_ANY_TABLE: "view_group_column_in_any_table",
    CREATE_GROUP: "create_group",
    UPDATE_GROUP: "edit_group",
    DELETE_GROUP: "delete_group",
    CREATE_INVITATION: "create_invitation",
    VIEW_INVITATIONS: "view_invitations",
    // Theme assignment on /groups/edit/[groupId]. Until a seed migration
    // adds this row to sf_permissions, isPermitted falls through to
    // has_all_permissions, so super_admins see the section and everyone
    // else does not. Granting it to other roles becomes possible once the
    // row exists.
    MANAGE_GROUP_THEME: "manage_group_theme",
  },
  CLIENT_PERMISSIONS: {
    MANAGE_CONVERSATION_APPS: "client_manage_conversation_apps",
    ASSIGN_ANONYMOUS_INTERVAL: "assign_anonymous_interval_to_client",
    ASSIGN_CLIENT_TO_USER: "assign_client_to_user",
    ASSIGN_SCENARIOS_TO_CLIENT: "assign_scenarios_to_client",
    VIEW_CLIENT_LIMITS: "view_client_limits",
    MANAGE_CLIENT_LIMITS: "manage_client_limits",
    CREATE_CLIENT: "create_client",
    UPDATE_CLIENT: "edit_client",
    DELETE_CLIENT: "delete_client",
    // Theme assignment on /clients/edit/[clientId]. Until a seed migration
    // adds this row to sf_permissions, isPermitted falls through to
    // has_all_permissions, so super_admins see the section and everyone
    // else does not. Granting it to other roles becomes possible once the
    // row exists.
    MANAGE_CLIENT_THEME: "manage_client_theme",
  },
  USER_PERMISSIONS: {
    CREATE_USER: "create_user",
    UPDATE_USER: "edit_user",
    DELETE_USER: "delete_user",
    GRANT_USER_PERMISSIONS: "grant_user_permissions",
    IMPERSONATE_USER: "impersonate_user",
  },
  ROLE_PERMISSIONS: {
    CREATE_ROLE: "create_role",
    UPDATE_ROLE: "update_role",
    DELETE_ROLE: "delete_role",
  },
  CONVERSATION_PERMISSIONS: {
    // UPDATE_CONVERSATION: "edit_conversation",
    DELETE_CONVERSATION: "delete_conversation",
  },
  TALKS_FORMS_PERMISSIONS: {
    VIEW_ALL_USER_FORMS: "view_all_user_forms",
    VIEW_CLIENT_USER_FORMS: "view_client_user_forms",
    VIEW_GROUP_USER_FORMS: "view_group_user_forms",
    /**
     * Edit a TALK somebody else created, scoped by the CREATOR's client / group.
     * Enforced server-side; the tiles endpoint folds them into `is_editable`, so
     * no component reads these directly. Deliberately not named *_user_forms —
     * that suffix belongs to the wizard's draft table (sf_rel_skill_forms) while
     * these gate the talks editor.
     */
    EDIT_TALKS_ALL_CLIENT: "edit_talks_all_client",
    EDIT_TALKS_IN_GROUP: "edit_talks_in_group",
    EDIT_ACTOR_CONCEPT_PROMPT: "edit_actor_concept_prompt",
    MANUALLY_CREATE_ACTOR: "manually_create_actor",
    EDIT_ONBOARDING_SCREEN_TEMPLATE:
      "edit_onboarding_screen_template_in_talks_editor",
    DELETE_ACTOR: "delete_actor",
    ASSIGN_CLIENT_GROUP: "assign_client_group_in_talks_editor",
  },
  TALKS_WIZARD_PERMISSIONS: {
    /** When true: user sees a skill selector (all active skills). When false: form loads with Default skill only, no selector. */
    TALKS_WIZARD_CHOOSE_SKILL: "talks_wizard_choose_skill",
  },
  TOP_HEADER_START_CALL: "top_header_start_call",
  PRE_CALL_UPDATE_USERNAME: "pre_call_update_user_name",
  /** Content → Relations: manage client–scenario and scenario–actor relations. Always allowed for super_admin. */
  VIEW_RELATIONS: "view_relations",
  SIDEBAR_CONTENT_RELATIONS: "sidebar_content_relations",
  /** Maintenance → Audit trail */
  VIEW_AUDIT_LOGS: "view_audit_logs",
  SIDEBAR_MAINTENANCE_AUDIT_TRAIL: "sidebar_management_audit_logs",
  MANAGE_AI_INCIDENTS: "manage_ai_incidents",
  SIDEBAR_MAINTENANCE_AI_INCIDENTS: "sidebar_maintenance_ai_incidents",
  /** Maintenance → AI usage */
  VIEW_AI_USAGE: "view_ai_usage",
  SIDEBAR_MAINTENANCE_AI_USAGE: "sidebar_management_ai_usage",
  /** Maintenance → Portal configs */
  MANAGE_PORTAL_CONFIGS: "manage_portal_configs",
  /** Maintenance → Table maintenance (CRUD on sf_ voices, language_map, etc.) */
  MAINTENANCE_TABLES: "maintenance_tables",
  SIDEBAR_MAINTENANCE_TABLES: "sidebar_maintenance_tables",
  /** Maintenance → System configuration (cron status + start/stop + run cleanup now) */
  MANAGE_CRON_JOBS: "manage_cron_jobs",
  /** Maintenance → Actor Bulk Change (bulk AI prompt updates). Gates both the menu item and the page. */
  ACTOR_BULK_CHANGE: "actor_bulk_change",
  /** Report Management */
  SIDEBAR_REPORT_MANAGEMENT: "sidebar_report_management",
  REPORT_MANAGEMENT_PERMISSIONS: {
    EDITOR: "report_editor",
    GROUP_VIEW: "report_group_view",
    GROUP_EDIT: "report_group_edit",
    GROUP_CREATE: "report_group_create",
    GROUP_DELETE: "report_group_delete",
    CLIENT_VIEW: "report_client_view",
    CLIENT_EDIT: "report_client_edit",
    CLIENT_CREATE: "report_client_create",
    CLIENT_DELETE: "report_client_delete",
  },
  /** Report Templates (Wave 3) — separate menu + perms from Report Management */
  SIDEBAR_REPORT_TEMPLATES: "sidebar_report_templates",
  REPORT_TEMPLATES_PERMISSIONS: {
    VIEW: "report_templates_view",
    /** @deprecated Wave 6: prefer CREATE / GROUP_EDIT / CLIENT_EDIT tiered keys. */
    EDIT: "report_templates_edit",
    CLIENT_LINK: "report_templates_client_link",
    GROUP_LINK: "report_templates_group_link",
    // Wave 6: tiered creator-based edit permissions for templates.
    CREATE: "report_templates_create",
    GROUP_EDIT: "report_templates_group_edit",
    CLIENT_EDIT: "report_templates_client_edit",
    // Wave 8a: Azure↔local sync button.
    SYNC: "report_templates_sync",
  },
};
